/**
 * The "WhatsApp broadcasts" entry in Chatwoot's own Campaigns group.
 *
 * `Sidebar.vue` gets one import and one line, both inside a `>>> WASLO DEVIATION >>>`
 * fence; everything that can change lives here, where upstream cannot reach it. THE PLAN,
 * §4 P10 and §6 R-1.
 *
 * WHY THIS IS NOT IN `wasloai/nav.js`, which is where the OTHER Waslo sidebar entry lives:
 * that file builds the Waslo AI group and carries the block comment about a group header
 * and its first child having to point at the same route. This is not a Waslo group entry —
 * it is a child of a group Chatwoot owns, added beside Live chat, SMS and WhatsApp because
 * that is where the things that go OUT live. Keeping it here means the two are edited for
 * different reasons and neither can break the other on an upstream bump.
 *
 * NO `featureFlag`, and that is deliberate rather than an omission. The `meta` const in
 * `campaigns.routes.js` carries `FEATURE_FLAGS.CAMPAIGNS`, and the Waslo routes beside it
 * carry no flag at all — for exactly the reason written out in `wasloai/routes`: a flag is
 * why Captain renders for nobody on this community install, and this section will not
 * repeat it. `SidebarGroup` filters children by the permissions of the route each `to`
 * resolves to, so `permissions: ['administrator']` on the routes is the single gate.
 */

import { wasloT } from 'dashboard/routes/dashboard/wasloai/i18n.js';

/**
 * @param {(key: string) => string} t Chatwoot's global translator, from `Sidebar.vue`.
 * @param {(name: string, params?: object, query?: object) => object} accountScopedRoute
 * @param {string} [locale] The locale the rest of the screen is using. Absent → English.
 *   This row sits among three of Chatwoot's own, which are translated — an English label
 *   between "الدردشة المباشرة" and "واتساب" is the single most visible way to look bolted
 *   on. See `wasloai/nav.js` for why it is passed rather than looked up.
 * @returns {object} One `children` entry for the Campaigns group.
 */
export function wasloBroadcastsSidebarItem(t, accountScopedRoute, locale) {
  return {
    name: 'WasloBroadcasts',
    label: wasloT(t, 'WASLO.NAV.BROADCASTS', locale),
    to: accountScopedRoute('waslo_broadcasts'),
    // Compose and detail are the same destination as far as the sidebar is concerned:
    // without this the row un-highlights the moment the owner starts building a send, and
    // the sidebar claims she is nowhere.
    activeOn: [
      'waslo_broadcasts',
      'waslo_broadcast_new',
      'waslo_broadcast_detail',
    ],
  };
}

/**
 * The do-not-contact list, in the same Campaigns group and directly after broadcasts.
 *
 * WHY IT LEFT THE WASLO AI GROUP. It used to be the third child of the "Outbound API"
 * sub-group, which is the developer corner — so the one list in the product that is
 * purely an owner's concern sat behind the one caption written to warn owners off. It
 * also blocked that caption from being `collapsible`, because collapsing a group hides
 * its children and this is not a row anyone may lose. Here it sits beside the two things
 * that actually send to a list of people, which is when an owner thinks about it.
 *
 * NO ICON, deliberately: its three Chatwoot siblings in this group carry none, and a
 * lone glyph in a column of text labels is how a row announces it was bolted on.
 *
 * The route name does not move — `waslo_outbound_numbers` still resolves to the same
 * page under `/waslo/outbound/numbers`, so every existing link and bookmark still lands.
 *
 * @param {(key: string) => string} t Chatwoot's global translator, from `Sidebar.vue`.
 * @param {(name: string, params?: object, query?: object) => object} accountScopedRoute
 * @param {string} [locale] The locale the rest of the screen is using. Absent → English.
 * @returns {object} One `children` entry for the Campaigns group.
 */
export function wasloDoNotContactSidebarItem(t, accountScopedRoute, locale) {
  return {
    name: 'WasloDoNotContact',
    label: wasloT(t, 'WASLO.NAV.OUTBOUND_NUMBERS', locale),
    to: accountScopedRoute('waslo_outbound_numbers'),
    activeOn: ['waslo_outbound_numbers'],
  };
}

/**
 * Both Campaigns rows, or neither — the shape `Sidebar.vue` spreads.
 *
 * They are gated together and by the same answer as the Waslo AI group, because they are
 * the same product: broadcasts send through the assistant's WhatsApp number, and the
 * do-not-contact list is the opt-out for exactly those sends. A workspace with no
 * assistant has nothing to send from and nobody to suppress.
 *
 * ONE FUNCTION FOR THE PAIR, so a future third row cannot be added ungated by being added
 * to `Sidebar.vue` instead — the ungated path no longer exists there. See
 * `wasloai/composables/useWasloProvisioned.js`.
 *
 * @param {(key: string) => string} t
 * @param {(name: string, params?: object, query?: object) => object} accountScopedRoute
 * @param {string} [locale]
 * @param {boolean} [provisioned] Does this workspace have an assistant? Absent → no.
 * @returns {object[]} Both entries, or none.
 */
export function wasloCampaignsSidebarItems(
  t,
  accountScopedRoute,
  locale,
  provisioned
) {
  if (provisioned !== true) return [];
  return [
    wasloBroadcastsSidebarItem(t, accountScopedRoute, locale),
    wasloDoNotContactSidebarItem(t, accountScopedRoute, locale),
  ];
}

export default wasloBroadcastsSidebarItem;
